import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { InviteOption } from "../../models/InternalUser";
import AppText from "../Core/AppText/AppText";
import Checkbox from "../Core/Checkbox/Checkbox";
import Button from "../Core/Button/Button";
import Dropdown from "../Core/Dropdown/Dropdown";
import { KeyValue } from "../../models/KeyValue";
import { Flex } from "@chakra-ui/react";

interface Props {
	title: string;
	text: string;
	acceptButtonText: string;
	rejectButtonText: string;
	onReject: any;
	onAccept: any;
	email?: string;
	phoneNo?: string;
}

const SendContractModal = ({
	title,
	text,
	rejectButtonText,
	acceptButtonText,
	onAccept,
	onReject,
	email,
	phoneNo,
}: Props) => {
	const { t } = useTranslation();
	const [channel, setChannel] = useState<InviteOption>({
		mail: true,
		sms: true,
	});
	const [expireDate, setExpireDate] = useState<{ key: string; value: string }>({
		key: "10 dagar",
		value: getExpiryDate(10),
	});

	function getExpiryDate(days: number) {
		const now = new Date();
		let expiryDate;

		switch (days) {
			case 1:
				expiryDate = new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000);
				break;
			case 2:
				expiryDate = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);
				break;
			case 3:
				expiryDate = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
				break;
			case 4:
				expiryDate = new Date(now.getTime() + 4 * 24 * 60 * 60 * 1000);
				break;
			case 5:
				expiryDate = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);
				break;
			case 6:
				expiryDate = new Date(now.getTime() + 6 * 24 * 60 * 60 * 1000);
				break;
			case 7:
				expiryDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
				break;
			case 8:
				expiryDate = new Date(now.getTime() + 8 * 24 * 60 * 60 * 1000);
				break;
			case 9:
				expiryDate = new Date(now.getTime() + 9 * 24 * 60 * 60 * 1000);
				break;
			case 10:
				expiryDate = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000);
				break;
			default:
				throw new Error("Input must be a number between 1 and 10");
		}

		// Format as YYYY-MM-DD HH:mm:ss
		const year = expiryDate.getFullYear();
		const month = String(expiryDate.getMonth() + 1).padStart(2, "0");
		const date = String(expiryDate.getDate()).padStart(2, "0");
		const hours = String(expiryDate.getHours()).padStart(2, "0");
		const minutes = String(expiryDate.getMinutes()).padStart(2, "0");
		const seconds = String(expiryDate.getSeconds()).padStart(2, "0");

		return `${year}-${month}-${date} ${hours}:${minutes}:${seconds}`;
	}

	function triggerOnChange(valueInfo: string, value: number) {
		setExpireDate({ key: valueInfo, value: getExpiryDate(value) });
	}
	return (
		<ModalContainer>
			<ModalWrapper>
				<AppText size="xm" weight="bold">
					{title && t(title as any)}
				</AppText>
				<AppText margin="1.5rem 0 1rem 0" textAlign="center">
					{text && t(text as any)}
				</AppText>

				<Flex direction={"column"} width="100%" margin="0 0 1.5rem 0">
					<Dropdown<KeyValue, KeyValue>
						onChange={(o) => triggerOnChange(o.value, o.key as number)}
						getValue={(o) => o}
						value={expireDate.key}
						getOptionLabel={(option: KeyValue) => option.value}
						options={[
							{ key: 1, value: "1 dagar" },
							{ key: 2, value: "2 dagar" },
							{ key: 3, value: "3 dagar" },
							{ key: 4, value: "4 dagar" },
							{ key: 5, value: "5 dagar" },
							{ key: 6, value: "6 dagar" },
							{ key: 7, value: "7 dagar" },
							{ key: 8, value: "8 dagar" },
							{ key: 9, value: "9 dagar" },
							{ key: 10, value: "10 dagar" },
						]}
						label="Länkens giltighetstid"
						defaultValue={[{ key: 10, value: "10 dagar" }]}
						margin="0"
						mandatory
					/>
				</Flex>

				<div style={{ alignItems: "left" }}>
					{/* Readonly, email is required */}
					<Checkbox
						label={t("recruitment.sendContractModal.emailLabel", {
							email: email || "",
						})}
						isChecked
						disabled
						margin="0 0 15px 0"
					/>
					<Checkbox
						label={t("recruitment.sendContractModal.smsLabel", {
							phoneNo: phoneNo || "",
						})}
						isChecked={channel.sms}
						margin="0 0 15px 0"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							if (e.target.checked) {
								setChannel((inviteType) => ({ ...inviteType, sms: true }));
							} else {
								setChannel((inviteType) => ({ ...inviteType, sms: false }));
							}
						}}
					/>
				</div>
				<Button
					width="12.625rem"
					backgroundColor="primary"
					textColor="white"
					margin="1rem 0 0.5rem 0"
					onClick={() => onAccept(channel, expireDate.value)}
				>
					{t(acceptButtonText as any)}
				</Button>
				<Button
					width="12.625rem"
					backgroundColor="dividerGray"
					textColor="black"
					onClick={() => onReject()}
				>
					{t(rejectButtonText as any)}
				</Button>
			</ModalWrapper>
		</ModalContainer>
	);
};

export default SendContractModal;

const ModalContainer = styled.div`
	position: fixed;
	z-index: 1000;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	background: rgba(0, 0, 0, 0.6);
	overflow-y: auto;
`;

const ModalWrapper = styled.section`
	background: white;
	width: 100%;
	max-width: 32.5rem;
	margin: 10em auto 5em auto;
	height: auto;
	align-items: center;
	border-radius: 10px;
	display: flex;
	flex-direction: column;
	padding: 3.125rem;
`;
